'use client';
import React, { useState } from 'react';
import { Compass, Wrench, Users, Rocket, Target, Brain, Search, Paperclip, FlaskConical, Heart, Check, CheckCircle2, MapPin } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

const shipped: { icon: LucideIcon; title: string; desc: string }[] = [
  {
    icon: Rocket,
    title: 'Resume + Cover Letter Generation',
    desc: 'One-click documents customized to every job — standard, federal, and freelance modes.',
  },
  {
    icon: Target,
    title: 'ATS Match Scoring',
    desc: 'See your keyword match percentage and missing skills before you apply.',
  },
  {
    icon: Brain,
    title: 'Career Interview System',
    desc: 'Answer a few quick questions so our AI truly understands your experience.',
  },
  {
    icon: Search,
    title: 'Job Search & Tracking',
    desc: 'Find roles, track every application, get follow-up reminders, and compare offers.',
  },
  {
    icon: FlaskConical,
    title: 'AI Voice Mock Interviews',
    desc: '3D talking avatars, 6 round types, panel interviews, real-time scoring, and company-specific coaching for 40+ employers.',
  },
  {
    icon: Paperclip,
    title: 'LinkedIn Optimization Suite',
    desc: 'Profile scoring, section-by-section AI coaching, professional banner templates, and quick-win suggestions.',
  },
];

const upcoming: { icon: LucideIcon; title: string; desc: string }[] = [
];

export default function AboutContent() {
  const [expanded, setExpanded] = useState([false, false, false]);
  const handleToggle = (idx: number) => {
    setExpanded(prev => prev.map((v, i) => (i === idx ? !v : v)));
  };

  const cards: { icon: LucideIcon; title: string; summary: string; details: React.ReactNode }[] = [
    {
      icon: Compass,
      title: 'Our Mission',
      summary: 'Helping you stand out with smart, personalized, AI-powered tools that save time and get results.',
      details: (
        <div className="mt-2 text-black text-sm space-y-4 text-center">
          <p>We believe every professional deserves a resume that reflects who they are — not just what they&apos;ve done. But most resume tools treat everyone the same, offering generic templates and vague advice.</p>
          <p>Superpower Resume was built for real people in the real world — people changing careers, returning to the workforce, or chasing their next big opportunity.</p>
        </div>
      ),
    },
    {
      icon: Wrench,
      title: 'Why We Built This',
      summary: "We've been there—endlessly tweaking, pasting, and wondering why it never stands out. Superpower Resume is different.",
      details: (
        <div className="mt-2 text-black text-sm space-y-4 text-center">
          <p>We&apos;ve been there — endlessly tweaking bullet points, pasting the same resume into every job portal, and wondering why it never stands out.</p>
          <p>Traditional resume builders give you rigid templates. Generic language. A false sense of productivity.</p>
          <p>Superpower Resume is different. It pulls from your real experience — LinkedIn, past resumes, your own voice — and uses AI to tailor each application to the job you actually want.</p>
          <p>The result? A resume and cover letter that don&apos;t just look great — they work.</p>
        </div>
      ),
    },
    {
      icon: Users,
      title: 'Who This Is For',
      summary: 'Mid-career professionals who want more than cookie-cutter templates.',
      details: (
        <div className="mt-2 text-black text-sm space-y-4 text-left w-full max-w-xs mx-auto">
          <ul className="list-disc list-inside mb-2">
            <li>Career changers</li>
            <li>Experienced professionals re-entering the job market</li>
            <li>People applying for leadership or specialized roles</li>
            <li>Anyone who wants to save time while getting better results</li>
          </ul>
          <p>If you&apos;re tired of rewriting the same resume for every job, you&apos;re in the right place.</p>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-blue-50 via-white to-stone-100 py-16 px-4 flex flex-col items-center">
      <main className="w-full max-w-2xl mx-auto">
        <h1 className="text-4xl font-extrabold mb-10 text-center text-gray-900 tracking-tight">About Superpower Resume</h1>
      </main>
      {/* Mission/Why/Who Cards Row */}
      <div className="w-full max-w-5xl mx-auto">
        <div className="w-full flex flex-col md:flex-row gap-8 mb-12">
          {cards.map((card, idx) => {
            const Icon = card.icon;
            return (
              <section key={card.title} className="w-full flex-1 min-w-0 flex justify-center">
                <div className="relative w-full bg-white/90 rounded-2xl shadow-2xl px-8 py-12 flex flex-col items-center">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-1 rounded-b bg-gradient-to-r from-blue-500 to-blue-400 mb-6" />
                  <div className="mb-4 flex items-center justify-center">
                    <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-blue-400 text-white shadow-lg border-4 border-white">
                      <Icon className="w-8 h-8" />
                    </span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-extrabold mb-2 text-center text-gray-900 tracking-tight">{card.title}</h2>
                  <p className="text-base text-black mb-4 text-center">{card.summary}</p>
                  <button onClick={() => handleToggle(idx)} className="text-blue-700 font-semibold flex items-center gap-1 hover:underline mb-2 focus:outline-none">
                    {expanded[idx] ? 'Hide details' : 'Learn more'}
                    <span className={`transition-transform ${expanded[idx] ? 'rotate-180' : ''}`}>&#x25BC;</span>
                  </button>
                  {expanded[idx] && card.details}
                </div>
              </section>
            );
          })}
        </div>
      </div>
      <main className="w-full max-w-2xl mx-auto">
        <section>
          <h2 className="text-2xl font-bold mb-8 flex items-center gap-2">
            <CheckCircle2 className="w-6 h-6 text-green-500" />
            <span>What We&apos;ve Built</span>
          </h2>
          <ol className="relative border-l-4 border-green-200 ml-4 mb-12">
            {shipped.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.title} className="mb-10 ml-6">
                  <span className="absolute -left-7 flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg border-4 border-white">
                    <Icon className="w-6 h-6" />
                  </span>
                  <h3 className="text-xl font-semibold mb-1 mt-1 text-gray-900">{item.title}</h3>
                  <p className="text-black text-base">{item.desc}</p>
                </li>
              );
            })}
          </ol>
          {upcoming.length > 0 && (
            <>
              <h2 className="text-2xl font-bold mb-8 flex items-center gap-2">
                <MapPin className="w-6 h-6 text-blue-500" />
                <span>Coming Next</span>
              </h2>
              <ol className="relative border-l-4 border-blue-200 ml-4">
                {upcoming.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.title} className="mb-10 ml-6">
                      <span className="absolute -left-7 flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-blue-400 text-white shadow-lg border-4 border-white">
                        <Icon className="w-6 h-6" />
                      </span>
                      <h3 className="text-xl font-semibold mb-1 mt-1 text-gray-900">{item.title}</h3>
                      <p className="text-black text-base">{item.desc}</p>
                    </li>
                  );
                })}
              </ol>
            </>
          )}
        </section>
        <section className="mt-12">
          <h2 className="text-2xl font-bold mb-4">Who Builds This</h2>
          <p className="text-black text-base">
            Superpower Resume is built by the team behind{' '}
            <a
              href="https://inventivehq.com"
              className="text-blue-700 font-semibold hover:underline"
            >
              InventiveHQ
            </a>
            , which publishes a free library of IT and security tools. We build software the
            same way on both sides: no dark patterns, no paywall in front of the part you
            came for.
          </p>
        </section>
        <section className="mt-12 mb-2 flex justify-center">
          <div className="relative bg-gradient-to-br from-blue-100/60 to-stone-200/60 rounded-2xl shadow-xl border border-gray-100 px-8 py-12 max-w-xl w-full flex flex-col items-center group transition-all duration-300 hover:shadow-2xl">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-1 rounded-b bg-gradient-to-r from-blue-500 to-blue-400 mb-6" />
            <div className="mb-4 flex items-center justify-center">
              <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-blue-400 text-white shadow-lg border-4 border-white animate-pulse">
                <Heart className="w-8 h-8" />
              </span>
            </div>
            <h3 className="text-2xl font-extrabold mb-4 text-center text-gray-900">What We Believe</h3>
            <p className="text-lg text-black mb-6 text-center">We&apos;re building Superpower Resume on a few simple, powerful principles:</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 w-full max-w-lg mb-2">
              {[
                { text: "You shouldn't need to be a designer or writer", suffix: ' to get a job' },
                { text: 'AI should empower, not replace, your voice', suffix: '' },
                { text: 'Every resume should be tailored to the job', suffix: ' — without extra work' },
                { text: 'Professional tools should feel personal, fast, and frustration-free', suffix: '' },
              ].map(({ text, suffix }) => (
                <li key={text} className="flex items-start gap-2 text-base text-black">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span><b>{text}</b>{suffix}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>
    </div>
  );
}
